import React, { useState, useEffect, useCallback } from 'react';
import { useOutlet } from '../lib/OutletContext';
import {
  parseSalesFile, getSalesTemplates, saveSalesTemplate, deleteSalesTemplate,
  getItemMappings, saveItemMapping, ingestSalesUpload,
  getSalesUploadHistory, getUnmappedItems,
} from '../lib/api';
import {
  Upload, FileSpreadsheet, CheckCircle, AlertTriangle, X, ChevronRight,
  Trash2, Save, Play, Eye, History, Link2, RefreshCw,
} from 'lucide-react';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';

const SYSTEM_FIELDS = [
  { key: 'business_date', label: 'Business Date', required: true },
  { key: 'item_name', label: 'Item Name', required: true },
  { key: 'quantity_sold', label: 'Quantity Sold', required: true },
  { key: 'item_code', label: 'Item Code' },
  { key: 'gross_sales', label: 'Gross Sales' },
  { key: 'net_sales', label: 'Net Sales' },
  { key: 'category', label: 'Category' },
  { key: 'channel', label: 'Channel' },
  { key: 'session', label: 'Session' },
];

/* ── Tab system ──────────────────────────────────────────── */
const tabs = [
  { id: 'upload', label: 'Upload', icon: Upload },
  { id: 'history', label: 'History', icon: History },
  { id: 'mappings', label: 'Item Mappings', icon: Link2 },
  { id: 'templates', label: 'Templates', icon: FileSpreadsheet },
];

const SalesUpload = () => {
  const { selectedOutlet } = useOutlet();
  const [activeTab, setActiveTab] = useState('upload');

  return (
    <div className="space-y-6" data-testid="sales-upload-root">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Sales Data</h1>
        <p className="text-sm text-zinc-500 mt-1">Upload item-wise sales reports to drive consumption + procurement</p>
      </div>

      <div className="flex gap-1 bg-[#18181b] border border-[#27272a] rounded-lg p-1" data-testid="sales-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === t.id ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white hover:bg-[#27272a]'
            }`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'upload' && <UploadTab outletId={selectedOutlet} />}
      {activeTab === 'history' && <HistoryTab outletId={selectedOutlet} />}
      {activeTab === 'mappings' && <MappingsTab />}
      {activeTab === 'templates' && <TemplatesTab />}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   UPLOAD TAB — The main flow
   ═════════════════════════════════════════════════════════════ */
const UploadTab = ({ outletId }) => {
  const [step, setStep] = useState('select'); // select → map → preview → done
  const [file, setFile] = useState(null);
  const [parseResult, setParseResult] = useState(null);
  const [mapping, setMapping] = useState({});
  const [dryRunResult, setDryRunResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    getSalesTemplates().then(d => setTemplates(d.templates || [])).catch(() => {});
  }, []);

  const handleFileDrop = async (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (!f) return;
    setFile(f);
    setLoading(true);
    try {
      const result = await parseSalesFile(f);
      setParseResult(result);
      setMapping(result.suggested_mapping || {});
      setStep('map');
      toast.success(`Parsed ${result.total_rows} rows`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to parse file');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyTemplate = (template) => {
    setMapping(template.column_mapping);
    toast.success(`Applied template: ${template.template_name}`);
  };

  const handleSaveTemplate = async () => {
    const name = prompt('Template name:');
    if (!name) return;
    try {
      await saveSalesTemplate({ template_name: name, column_mapping: mapping, outlet_id: outletId });
      const d = await getSalesTemplates();
      setTemplates(d.templates || []);
      toast.success('Template saved');
    } catch { toast.error('Failed to save template'); }
  };

  const requiredMapped = ['business_date', 'item_name', 'quantity_sold'].every(f => mapping[f]);

  const handleValidate = async () => {
    if (!requiredMapped) { toast.error('Map all required fields first'); return; }
    setLoading(true);
    try {
      const result = await ingestSalesUpload({
        outlet_id: outletId,
        column_mapping: mapping,
        file_hash: parseResult.file_hash,
        file_name: parseResult.file_name,
        dry_run: true,
      });
      setDryRunResult(result);
      setStep('preview');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Validation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleIngest = async () => {
    setLoading(true);
    try {
      const result = await ingestSalesUpload({
        outlet_id: outletId,
        column_mapping: mapping,
        file_hash: parseResult.file_hash,
        file_name: parseResult.file_name,
        dry_run: false,
      });
      if (result.success) {
        toast.success(`Ingested ${result.rows_ingested} rows → ${result.consumption_entries} consumption entries`);
        setStep('done');
      } else {
        toast.error(result.message || 'Ingestion failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Ingestion failed');
    } finally {
      setLoading(false);
    }
  };

  const resetFlow = () => {
    setStep('select');
    setFile(null);
    setParseResult(null);
    setMapping({});
    setDryRunResult(null);
  };

  return (
    <div className="space-y-4">
      {/* Progress bar */}
      <div className="flex items-center gap-2 text-xs" data-testid="upload-progress">
        {['select', 'map', 'preview', 'done'].map((s, i) => (
          <React.Fragment key={s}>
            {i > 0 && <ChevronRight className="w-3 h-3 text-zinc-600" />}
            <span className={`px-3 py-1 rounded-full font-medium ${
              step === s ? 'bg-emerald-600 text-white' : 'bg-[#27272a] text-zinc-500'
            }`}>{s === 'select' ? '1. Upload' : s === 'map' ? '2. Map Columns' : s === 'preview' ? '3. Preview' : '4. Done'}</span>
          </React.Fragment>
        ))}
        {step !== 'select' && (
          <button onClick={resetFlow} className="ml-auto text-zinc-500 hover:text-white transition-colors" data-testid="reset-upload-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Step 1: File Select */}
      {step === 'select' && (
        <div
          className="border-2 border-dashed border-[#27272a] rounded-xl p-12 text-center hover:border-emerald-500/50 transition-colors cursor-pointer"
          onDragOver={e => e.preventDefault()}
          onDrop={handleFileDrop}
          onClick={() => document.getElementById('sales-file-input').click()}
          data-testid="file-drop-zone"
        >
          <input id="sales-file-input" type="file" accept=".csv,.xlsx,.tsv" className="hidden" onChange={handleFileDrop} />
          {loading ? (
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-emerald-500 mx-auto" />
          ) : (
            <>
              <Upload className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
              <p className="text-lg font-medium text-zinc-300">Drop CSV or XLSX here</p>
              <p className="text-sm text-zinc-500 mt-1">or click to browse</p>
            </>
          )}
        </div>
      )}

      {/* Step 2: Column Mapping */}
      {step === 'map' && parseResult && (
        <div className="space-y-4">
          <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-400">Column Mapping</h3>
              <div className="flex gap-2">
                {templates.length > 0 && (
                  <select
                    className="bg-[#09090b] border border-[#27272a] rounded-lg px-3 py-1.5 text-xs text-zinc-300"
                    onChange={e => {
                      const t = templates.find(t => t.template_id === e.target.value);
                      if (t) handleApplyTemplate(t);
                    }}
                    defaultValue=""
                    data-testid="template-selector"
                  >
                    <option value="" disabled>Apply template...</option>
                    {templates.map(t => <option key={t.template_id} value={t.template_id}>{t.template_name}</option>)}
                  </select>
                )}
                <button onClick={handleSaveTemplate} className="px-3 py-1.5 bg-[#27272a] rounded-lg text-xs hover:bg-[#3f3f46] transition-colors flex items-center gap-1" data-testid="save-template-btn">
                  <Save className="w-3 h-3" /> Save Template
                </button>
              </div>
            </div>

            <div className="grid gap-3">
              {SYSTEM_FIELDS.map(sf => (
                <div key={sf.key} className="flex items-center gap-3">
                  <div className="w-40 text-sm">
                    <span className={sf.required ? 'text-white font-medium' : 'text-zinc-400'}>
                      {sf.label}
                    </span>
                    {sf.required && <span className="text-red-500 ml-1">*</span>}
                  </div>
                  <select
                    value={mapping[sf.key] || ''}
                    onChange={e => setMapping(prev => ({ ...prev, [sf.key]: e.target.value || undefined }))}
                    className="flex-1 bg-[#09090b] border border-[#27272a] rounded-lg px-3 py-2 text-sm text-zinc-300"
                    data-testid={`mapping-${sf.key}`}
                  >
                    <option value="">— Not mapped —</option>
                    {parseResult.headers.map(h => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                  {mapping[sf.key] && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                </div>
              ))}
            </div>
          </div>

          {/* Data preview */}
          <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-5">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-400 mb-3">Data Preview ({parseResult.total_rows} rows)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    {parseResult.headers.map(h => (
                      <th key={h} className="text-left p-2 border-b border-[#27272a] text-zinc-500 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parseResult.preview_rows.slice(0, 5).map((row, i) => (
                    <tr key={`prow-${i}`} className="hover:bg-[#09090b]">
                      {parseResult.headers.map(h => (
                        <td key={h} className="p-2 border-b border-[#27272a]/50 text-zinc-300">{row[h]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <button
            onClick={handleValidate}
            disabled={!requiredMapped || loading}
            className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            data-testid="validate-btn"
          >
            {loading ? <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" /> : <Eye className="w-4 h-4" />}
            Validate & Preview
          </button>
        </div>
      )}

      {/* Step 3: Preview + Ingest */}
      {step === 'preview' && dryRunResult && (
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="validation-summary">
            <div className="bg-[#09090b] border border-emerald-500/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold font-mono text-emerald-500">{dryRunResult.valid_rows}</p>
              <p className="text-xs text-zinc-500">Valid Rows</p>
            </div>
            <div className="bg-[#09090b] border border-red-500/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold font-mono text-red-500">{dryRunResult.invalid_rows}</p>
              <p className="text-xs text-zinc-500">Invalid Rows</p>
            </div>
            <div className="bg-[#09090b] border border-blue-500/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold font-mono text-blue-500">{dryRunResult.rows_with_recipe}</p>
              <p className="text-xs text-zinc-500">Mapped to Recipes</p>
            </div>
            <div className="bg-[#09090b] border border-amber-500/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold font-mono text-amber-500">{dryRunResult.unmapped_items?.length || 0}</p>
              <p className="text-xs text-zinc-500">Unmapped Items</p>
            </div>
          </div>

          {/* Unmapped items warning */}
          {dryRunResult.unmapped_items?.length > 0 && (
            <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <h4 className="text-sm font-medium text-amber-500">Unmapped Items (will be skipped)</h4>
              </div>
              <div className="flex flex-wrap gap-2">
                {dryRunResult.unmapped_items.map(item => (
                  <span key={item} className="px-2 py-1 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-300">{item}</span>
                ))}
              </div>
            </div>
          )}

          {/* Consumption preview */}
          {dryRunResult.preview_consumption?.length > 0 && (
            <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-400 mb-3">Consumption Preview</h3>
              <div className="space-y-1.5">
                {dryRunResult.preview_consumption.map((e, i) => (
                  <div key={e.ingredient_id || `pc-${i}`} className="flex items-center justify-between p-2.5 bg-[#09090b] border border-[#27272a] rounded-lg">
                    <span className="text-sm">{e.ingredient_name}</span>
                    <span className="text-sm font-mono text-red-400">-{e.qty} {e.unit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Validation errors */}
          {dryRunResult.validation_errors?.length > 0 && (
            <div className="bg-red-500/5 border border-red-500/30 rounded-xl p-4">
              <h4 className="text-sm font-medium text-red-500 mb-2">Validation Errors</h4>
              {dryRunResult.validation_errors.slice(0, 5).map((e, i) => (
                <p key={`ve-${e.row}-${i}`} className="text-xs text-red-300">Row {e.row}: {e.errors.join(', ')}</p>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => setStep('map')} className="py-3 bg-[#27272a] hover:bg-[#3f3f46] rounded-lg font-medium transition-colors" data-testid="back-to-mapping-btn">
              Back to Mapping
            </button>
            <button
              onClick={handleIngest}
              disabled={loading || dryRunResult.rows_with_recipe === 0}
              className="py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              data-testid="ingest-btn"
            >
              {loading ? <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" /> : <Play className="w-4 h-4" />}
              Ingest {dryRunResult.rows_with_recipe} Rows
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Done */}
      {step === 'done' && (
        <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-xl p-8 text-center" data-testid="ingest-success">
          <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <h3 className="text-xl font-bold text-emerald-500">Upload Ingested Successfully</h3>
          <p className="text-sm text-zinc-400 mt-2">Consumption entries have been created and inventory balances updated.</p>
          <button onClick={resetFlow} className="mt-6 px-6 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-medium transition-colors" data-testid="upload-another-btn">
            Upload Another File
          </button>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   HISTORY TAB
   ═════════════════════════════════════════════════════════════ */
const HistoryTab = ({ outletId }) => {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSalesUploadHistory(outletId)
      .then(d => setUploads(d.uploads || []))
      .catch(() => toast.error('Failed to load history'))
      .finally(() => setLoading(false));
  }, [outletId]);

  if (loading) return <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500 mx-auto mt-12" />;

  return (
    <div className="space-y-3" data-testid="history-tab">
      {uploads.length === 0 ? (
        <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-12 text-center">
          <History className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No uploads yet</p>
        </div>
      ) : (
        uploads.map(u => (
          <div key={u.upload_id} className="bg-[#18181b] border border-[#27272a] rounded-xl p-4 flex items-center justify-between hover:border-[#3f3f46] transition-colors">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-5 h-5 text-zinc-500" />
              <div>
                <p className="text-sm font-medium">{u.file_name}</p>
                <p className="text-xs text-zinc-500">
                  {formatDateTime(u.ingested_at)} &middot; by {u.uploaded_by_email}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-right">
              <div>
                <p className="text-sm font-mono">{u.rows_ingested}/{u.rows_total} rows</p>
                <p className="text-xs text-zinc-500">{u.consumption_entries} entries</p>
              </div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                u.status === 'INGESTED' ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/30'
                : 'bg-amber-500/10 text-amber-500 border border-amber-500/30'
              }`}>{u.status}</span>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   MAPPINGS TAB
   ═════════════════════════════════════════════════════════════ */
const MappingsTab = () => {
  const [mappings, setMappings] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState('');
  const [newRecipe, setNewRecipe] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [m, u] = await Promise.all([getItemMappings(), getUnmappedItems()]);
      setMappings(m.mappings || []);
      setUnmapped(u.unmapped_items || []);
    } catch { toast.error('Failed to load mappings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAdd = async () => {
    if (!newItem || !newRecipe) { toast.error('Fill both fields'); return; }
    try {
      await saveItemMapping({ item_name: newItem, recipe_id: newRecipe, recipe_name: newRecipe });
      setNewItem('');
      setNewRecipe('');
      loadData();
      toast.success('Mapping saved');
    } catch { toast.error('Failed to save'); }
  };

  if (loading) return <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500 mx-auto mt-12" />;

  return (
    <div className="space-y-4" data-testid="mappings-tab">
      {/* Add new mapping */}
      <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-4">
        <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-3">Add Item Mapping</h3>
        <div className="flex gap-3">
          <input
            value={newItem}
            onChange={e => setNewItem(e.target.value)}
            placeholder="Sales item name (e.g. Paneer Tikka)"
            className="flex-1 bg-[#09090b] border border-[#27272a] rounded-lg px-3 py-2 text-sm text-zinc-300 placeholder-zinc-600"
            data-testid="new-mapping-item"
          />
          <input
            value={newRecipe}
            onChange={e => setNewRecipe(e.target.value)}
            placeholder="Recipe ID or name"
            className="flex-1 bg-[#09090b] border border-[#27272a] rounded-lg px-3 py-2 text-sm text-zinc-300 placeholder-zinc-600"
            data-testid="new-mapping-recipe"
          />
          <button onClick={handleAdd} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm font-medium transition-colors" data-testid="add-mapping-btn">
            Add
          </button>
        </div>
      </div>

      {/* Existing mappings */}
      <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-4">
        <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-3">Active Mappings ({mappings.length})</h3>
        {mappings.length === 0 ? (
          <p className="text-sm text-zinc-500">No mappings defined yet</p>
        ) : (
          <div className="space-y-1.5">
            {mappings.map(m => (
              <div key={m.item_name} className="flex items-center justify-between p-2.5 bg-[#09090b] border border-[#27272a] rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{m.item_name}</span>
                  <ChevronRight className="w-3 h-3 text-zinc-600" />
                  <span className="text-sm text-emerald-500">{m.recipe_name || m.recipe_id}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Unmapped items */}
      {unmapped.length > 0 && (
        <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-amber-500 mb-3">Unmapped Items ({unmapped.length})</h3>
          <div className="space-y-1.5">
            {unmapped.map(u => (
              <div key={u.item_name} className="flex items-center justify-between p-2.5 bg-[#09090b] border border-amber-500/20 rounded-lg">
                <span className="text-sm">{u.item_name}</span>
                <span className="text-xs text-zinc-500">seen {u.upload_count}x</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   TEMPLATES TAB
   ═════════════════════════════════════════════════════════════ */
const TemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadTemplates = useCallback(async () => {
    try {
      const d = await getSalesTemplates();
      setTemplates(d.templates || []);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await deleteSalesTemplate(id);
      loadTemplates();
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500 mx-auto mt-12" />;

  return (
    <div className="space-y-3" data-testid="templates-tab">
      {templates.length === 0 ? (
        <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-12 text-center">
          <FileSpreadsheet className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No templates yet. Upload a file and save a column mapping to create one.</p>
        </div>
      ) : (
        templates.map(t => (
          <div key={t.template_id} className="bg-[#18181b] border border-[#27272a] rounded-xl p-4 flex items-center justify-between hover:border-[#3f3f46] transition-colors">
            <div>
              <p className="text-sm font-medium">{t.template_name}</p>
              <p className="text-xs text-zinc-500 mt-1">
                {Object.entries(t.column_mapping || {}).map(([k, v]) => `${k}→${v}`).join(' | ')}
              </p>
            </div>
            <button onClick={() => handleDelete(t.template_id)} className="p-2 hover:bg-red-500/10 rounded-lg transition-colors text-zinc-500 hover:text-red-500" data-testid={`delete-template-${t.template_id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))
      )}
    </div>
  );
};

export default SalesUpload;
